import { apiCustomFields } from "@/api/custom-fields.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { appKeys, useAppConfig } from "@/queries/settings/use-app.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiCFTableRowFilter,
  ApiCFTabSortItem,
  ApiCFTabType,
  ApiField,
  ApiFieldDefaultValuePayload,
  ApiFieldPayload,
  ApiSystemFieldPayload,
  ApiFieldTypes,
  ApiFieldValueTypes,
  ApiCFEntityTypes,
  ApiSectionPayload,
  ApiTableFieldPayload,
  ApiTablePayload,
  ApiTable,
  ApiTableTypes,
} from "@/types/custom-field.types.ts";
import { ApiAppConfigUIEmployeeProfileSection } from "@/types/config.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Control, FieldValues } from "react-hook-form";

export const customFieldsKeys = createQueryKeys("sections", {
  entityConfig: (entityType: ApiCFEntityTypes) => ({
    queryKey: [entityType],
    queryFn: async () => {
      const result = await apiCustomFields.getEntityConfig(entityType);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  fieldsByType: (type: ApiCFEntityTypes) => ({
    queryKey: [type],
    queryFn: async () => {
      const result = await apiCustomFields.getFieldsByType(type);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  sectionsByType: (type: ApiCFEntityTypes) => ({
    queryKey: [type],
    queryFn: async () => {
      const result = await apiCustomFields.getSectionsByType(type);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  fieldsByTableId: (tableId: number) => ({
    queryKey: [tableId],
    queryFn: async () => {
      const result = await apiCustomFields.getFieldsByTableId(tableId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  allTables: {
    queryKey: null,
    queryFn: async () => {
      const result = await apiCustomFields.getAllTables();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  tableRows: (tableId: number, filter: ApiCFTableRowFilter) => ({
    queryKey: [tableId, filter],
    queryFn: async () => {
      const result = await apiCustomFields.searchTableRows(tableId, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  tabsByEntityType: (entityType: ApiCFEntityTypes) => ({
    queryKey: [entityType],
    queryFn: async () => {
      const result = await apiCustomFields.getTabsByEntityType(entityType);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  checkFieldTitle: (title: string, entityType: ApiCFEntityTypes, field_id?: number) => ({
    queryKey: [{ title, entityType, field_id }],
    queryFn: async () => {
      const result = await apiCustomFields.check(title, entityType, field_id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  checkTableTitle: (title: string, field_id?: number) => ({
    queryKey: [{ title, field_id }],
    queryFn: async () => {
      const result = await apiCustomFields.checkTable(title, field_id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useSectionFields = (type: ApiCFEntityTypes, enabled: boolean = true) => {
  return useQuery({
    ...customFieldsKeys.fieldsByType(type),
    enabled,
  });
};

export const useTableFields = (tableId: number, enabled: boolean = true) => {
  return useQuery({
    ...customFieldsKeys.fieldsByTableId(tableId),
    enabled,
  });
};

export const useSectionsByType = (type: ApiCFEntityTypes) => {
  return useQuery({
    ...customFieldsKeys.sectionsByType(type),
  });
};

export const useTableRows = (
  tableId: number,
  filter: ApiCFTableRowFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...customFieldsKeys.tableRows(tableId, filter),
    enabled,
  });
};

export const useAllTables = (enabled: boolean = true) => {
  return useQuery({
    ...customFieldsKeys.allTables,
    enabled,
  });
};

export const useAllCustomTables = (enabled: boolean = true) => {
  const { data, ...queryState } = useAllTables(enabled);

  const customTables = useMemo(() => {
    return data?.filter((t: ApiTable) => t.table_type === ApiTableTypes.custom);
  }, [data]);

  return { data: customTables, ...queryState };
};

interface SaveTableParams {
  id: number | undefined;
  payload: ApiTablePayload;
}

export const useTableSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: SaveTableParams) => {
      const result = id
        ? await apiCustomFields.updateTable(id, payload)
        : await apiCustomFields.createTable(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (table) => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.allTables.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByTableId(table.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.tabsByEntityType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTableDelete = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCustomFields.deleteTable(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.allTables.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.tabsByEntityType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTableCheckSlug = (title: string, field_id?: number, enabled: boolean = true) => {
  return useQuery({
    ...customFieldsKeys.checkTableTitle(title, field_id),
    enabled: enabled && title.length > 0,
  });
};

export const useTabsByEntityType = (entityType: ApiCFEntityTypes, enabled: boolean = true) => {
  return useQuery({
    ...customFieldsKeys.tabsByEntityType(entityType),
    enabled,
  });
};

export const useTabByTypeAndId = (tabId: number, type: ApiCFEntityTypes) => {
  const { data: tabs } = useTabsByEntityType(type);

  return useMemo(() => {
    return tabs?.find((t) => t.id === tabId);
  }, [tabs, tabId]);
};

export const useSectionsByTabId = (tabId: number, type: ApiCFEntityTypes) => {
  const { data: sections } = useSectionsByType(type);

  return useMemo(() => {
    return sections?.filter((s) => s.tab_id === tabId) ?? [];
  }, [sections, tabId]);
};

interface SaveSectionParams {
  id: number | undefined;
  payload: ApiSectionPayload;
}

export const useSectionSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: SaveSectionParams) => {
      const result = id
        ? await apiCustomFields.updateSection(id, payload)
        : await apiCustomFields.createSection(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.sectionsByType._def,
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.tabsByEntityType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSectionsDelete = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCustomFields.deleteSection(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.sectionsByType._def,
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByType._def,
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.tabsByEntityType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTabItemsSort = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      type,
      entityType,
      items,
    }: {
      type: ApiCFTabType;
      entityType: ApiCFEntityTypes;
      items: ApiCFTabSortItem[];
    }) => {
      const result = await apiCustomFields.updateTabItemsSort(type, entityType, items);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.tabsByEntityType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export interface ApiFieldsSort {
  section_id: number;
  field_ids: number[];
}

export interface ApiTableFieldSort {
  field_ids: number[];
}

export const useFieldsSort = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: ApiFieldsSort[]) => {
      const result = await apiCustomFields.updateFieldsSort(items);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

interface SortTableFieldsParams {
  tableId: number;
  payload: ApiTableFieldSort;
}

export const useTableFieldsSort = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tableId, payload }: SortTableFieldsParams) => {
      const result = await apiCustomFields.updateTableFieldsSort(tableId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByTableId._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useFieldsCheckSlug = (
  title: string,
  entityType: ApiCFEntityTypes,
  field_id?: number
) => {
  return useQuery({
    ...customFieldsKeys.checkFieldTitle(title, entityType, field_id),
    enabled: title.length > 0,
  });
};

type SaveFieldParams =
  | { id: number; payload: ApiFieldPayload | ApiSystemFieldPayload }
  | { id?: undefined; payload: ApiFieldPayload };

type SaveTableFieldParams =
  | {
      tableId: number;
      fieldId: number;
      payload: ApiTableFieldPayload | ApiSystemFieldPayload;
    }
  | { tableId: number; fieldId?: undefined; payload: ApiTableFieldPayload };

export const useFieldSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SaveFieldParams) => {
      const result = params.id
        ? await apiCustomFields.updateField(params.id, params.payload)
        : await apiCustomFields.createField(params.payload as ApiFieldPayload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTableFieldSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SaveTableFieldParams) => {
      const result = params.fieldId
        ? await apiCustomFields.updateTableField(params.tableId, params.fieldId, params.payload)
        : await apiCustomFields.createTableField(
            params.tableId,
            params.payload as ApiTableFieldPayload
          );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByTableId._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useFieldDelete = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCustomFields.deleteField(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

interface DeleteTableFieldParams {
  tableId: number;
  fieldId: number;
}

export const useTableFieldDelete = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tableId, fieldId }: DeleteTableFieldParams) => {
      const result = await apiCustomFields.deleteTableField(tableId, fieldId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByTableId._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useFieldDefaultValueDelete = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCustomFields.deleteDefaultValue(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

interface SaveFieldDefaultValueParams {
  id: number;
  payload: ApiFieldDefaultValuePayload;
}

export const useFieldDefaultValueSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: SaveFieldDefaultValueParams) => {
      const result = await apiCustomFields.updateDefaultValue(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys.fieldsByType._def,
      });
      queryClient.invalidateQueries({ queryKey: customFieldsKeys.entityConfig._def });
      queryClient.invalidateQueries({
        queryKey: appKeys.config.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeeReferenceCustomFields = (enabled: boolean = true) => {
  const { data: config, ...queryState } = useAppConfig(enabled);

  const data: ApiField[] = useMemo(() => {
    const allSections: ApiAppConfigUIEmployeeProfileSection[] =
      config?.ui?.employee_profile?.tabs?.flatMap((tabWrapper) =>
        tabWrapper.items
          .map((item) => item.section)
          .filter((section): section is ApiAppConfigUIEmployeeProfileSection => section !== null)
      ) ?? [];

    return allSections
      .flatMap((s) => s.fields ?? [])
      .filter(
        (f) =>
          f.type === ApiFieldTypes.custom && f.field_type === ApiFieldValueTypes.employee_reference
      );
  }, [config]);

  return { data, ...queryState };
};

export const useEmployeeSelectCustomFields = (enabled: boolean = true) => {
  const { data: config, ...queryState } = useAppConfig(enabled);

  const data: ApiField[] = useMemo(() => {
    const allSections: ApiAppConfigUIEmployeeProfileSection[] =
      config?.ui?.employee_profile?.tabs?.flatMap((tabWrapper) =>
        tabWrapper.items
          .map((item) => item.section)
          .filter((section): section is ApiAppConfigUIEmployeeProfileSection => section !== null)
      ) ?? [];

    return allSections
      .flatMap((s) => s.fields ?? [])
      .filter((f) => f.type === ApiFieldTypes.custom && f.field_type === ApiFieldValueTypes.select);
  }, [config]);

  return { data, ...queryState };
};

export const useFieldById = (fieldId: number, type: ApiCFEntityTypes) => {
  const { data: fields, ...queryState } = useSectionFields(type);

  const field = useMemo(() => {
    if (!fields) return undefined;
    return fields.find((f: ApiField) => f.id === fieldId);
  }, [fields, fieldId]);

  return { field, ...queryState };
};

export const useTableById = (tableId: number) => {
  const { data: tables, ...queryState } = useAllTables();

  const table = useMemo(() => {
    if (!tables) return undefined;
    return tables.find((t: ApiTable) => t.id === tableId);
  }, [tables, tableId]);

  return { table, ...queryState };
};

export const useEntityFormConfig = (entityType: ApiCFEntityTypes) => {
  const query = useQuery({ ...customFieldsKeys.entityConfig(entityType), staleTime: 60 * 1000 });
  const sections = useMemo(
    () =>
      (query.data?.sections ?? []).map((section) => ({
        ...section,
        entity_type: entityType,
        tab_id: 0,
        is_archived: false,
        is_hidden: false,
        datetime_created_at: new Date(0),
        fields: section.fields.map((field): ApiField => ({
          ...field,
          section_id: section.id,
          entity_type: entityType,
          hint: field.hint ?? "",
          description: field.description ?? "",
          is_hidden: false,
          is_archived: false,
          is_historical: false,
          is_always_visible: field.is_always_visible ?? false,
          table_id: 0,
          config: field.config ?? undefined,
          default_value: field.default_value ?? undefined,
          options: field.options.map((option) => ({
            ...option,
            description: option.description ?? "",
          })),
        })),
      })),
    [query.data, entityType]
  );
  const fields = useMemo(() => sections.flatMap((section) => section.fields), [sections]);
  const customFields = useMemo(
    () => fields.filter((field) => field.type === ApiFieldTypes.custom),
    [fields]
  );
  const hasField = useCallback(
    (type: ApiFieldTypes) => fields.some((field) => field.type === type),
    [fields]
  );
  return {
    sections,
    fields,
    customFields,
    hasField,
    isPending: query.isPending,
    isSuccess: query.isSuccess,
    isError: query.isError,
  };
};
